import { Tooltip, TooltipCode, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import type { ModelDecode } from '@rock/models';
import { Info } from 'lucide-react';
import type { FC } from 'react';

/**
 * The ℹ️ beside a model number when its characters actually decode — what the manufacturer's own naming
 * scheme says the product is, segment by segment.
 *
 * The ICON's presence is half the signal: it appears only on a successful decode, so at a glance it
 * answers "did we read this number?" without claiming any screen space on the models we can't. That is
 * why this renders nothing rather than a "not decoded" state — most models have no scheme yet, and
 * captioning every one of them would bury the few that do.
 *
 * Nothing here restates the product type on its own line: `segments` already carries it as the platform
 * field's meaning, so the breakdown shows WHAT we learned and WHERE it came from in one pass — and the
 * equipment card avoids saying "top load washer" next to its own coarser catalog type.
 */
export const ModelDecodeInfo: FC<{ decode: ModelDecode | undefined }> = ({ decode }) => {
    if (!decode) return null;
    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <button
                    type="button"
                    aria-label="How this model number was decoded"
                    className="align-middle text-muted-foreground hover:text-foreground"
                >
                    <Info className="h-3 w-3" />
                </button>
            </TooltipTrigger>
            <TooltipContent className="text-pretty max-w-[340px] leading-snug">
                <div className="space-y-1">
                    {decode.segments.map((segment) => (
                        <div key={`${segment.label}-${segment.value}`} className="flex gap-2">
                            <TooltipCode>{segment.value}</TooltipCode>
                            <span>
                                {segment.label}
                                {segment.meaning ? `: ${segment.meaning}` : null}
                            </span>
                        </div>
                    ))}
                    {decode.notes.map((note) => (
                        <div key={note} className="opacity-80">
                            {note}
                        </div>
                    ))}
                </div>
            </TooltipContent>
        </Tooltip>
    );
};
